/**
 * Sample Data Seeding Service
 *
 * Seeds realistic demo data for new teams so users can immediately
 * understand ModCar without setup friction.
 *
 * Key features:
 * - One-time seeding per team (guarded by hasSampleData flag)
 * - Deterministic IDs for easy deletion
 * - All documents marked with isSample: true + seedRunId for tracking
 * - Dates relative to team.createdAt (baseDate) for realistic data
 * - All invoices use status: 'concept' to ensure deletability
 * - Respects Free plan limits (max 10 per collection)
 * - No dependency on trial logic
 *
 * DASHBOARD METRICS IMPACT:
 * ─────────────────────────
 * 1. Throughput (Doorstroom) - LEFT ring → ORANGE/RED
 *    - 2 vehicles have purchaseDate 60-75 days in the past
 *    - These "aged" vehicles drag down the Throughput score
 *    - 3 vehicles are recent (7-21 days) for realism
 *
 * 2. Today (Vandaag) - MIDDLE ring → GREEN
 *    - No sample manual tasks are created
 *    - Sample invoices are 'concept' status (not sent, no follow-ups)
 *    - All appointments are in the future
 *    - Result: 100% score (nothing overdue)
 *
 * 3. Liquidity (Liquiditeit) - RIGHT ring → GREEN (by design)
 *    - Sample invoices use status: 'concept' to remain deletable
 *    - Liquidity only counts 'sent' or 'overdue' invoices
 *    - TRADE-OFF: Deletability > Liquidity demo impact
 *    - To demo Liquidity orange/red, user must create real invoices
 */

import {
  doc,
  writeBatch,
  serverTimestamp,
  Timestamp,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { auth } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import type { BusinessType } from '@/types/onboarding';
import { generateCustomerSearchTokens, generateVehicleSearchTokens } from '@/lib/search/tokenize';
import {
  generatePurchaseInvoiceSearchFields,
  generateSalesInvoiceSearchFields,
} from '@/lib/search/invoiceTokenize';

// ============================================================================
// Pre-Seeding Verification
// ============================================================================

/**
 * Verify that the user has the required Firestore documents and permissions
 * before attempting to seed sample data.
 *
 * This prevents race conditions where seeding runs before:
 * - Member document is fully propagated
 * - User profile has activeTeamId set
 * - Team document exists
 */
export interface SeedReadinessResult {
  ready: boolean;
  teamExists: boolean;
  memberExists: boolean;
  memberRole: string | null;
  userHasActiveTeamId: boolean;
  currentUid: string | null;
  error?: string;
}

export async function verifySeedReadiness(teamId: string): Promise<SeedReadinessResult> {
  const currentUser = auth.currentUser;
  const uid = currentUser?.uid || null;

  const result: SeedReadinessResult = {
    ready: false,
    teamExists: false,
    memberExists: false,
    memberRole: null,
    userHasActiveTeamId: false,
    currentUid: uid,
  };

  if (!uid) {
    result.error = 'No authenticated user';
    logger.warn('[SampleData] verifySeedReadiness: No authenticated user');
    return result;
  }

  try {
    // Check 1: Team document exists
    const teamRef = doc(db, 'teams', teamId);
    const teamSnap = await getDoc(teamRef);
    result.teamExists = teamSnap.exists();

    if (!result.teamExists) {
      result.error = `Team document does not exist: teams/${teamId}`;
      logger.warn('[SampleData] verifySeedReadiness:', result.error);
      return result;
    }

    // Check 2: Member document exists with role
    const memberRef = doc(db, 'teams', teamId, 'members', uid);
    const memberSnap = await getDoc(memberRef);
    result.memberExists = memberSnap.exists();

    if (result.memberExists) {
      const memberData = memberSnap.data();
      result.memberRole = memberData?.role || null;
    }

    if (!result.memberExists) {
      result.error = `Member document does not exist: teams/${teamId}/members/${uid}`;
      logger.warn('[SampleData] verifySeedReadiness:', result.error);
      return result;
    }

    if (!result.memberRole) {
      result.error = `Member document has no role: teams/${teamId}/members/${uid}`;
      logger.warn('[SampleData] verifySeedReadiness:', result.error);
      return result;
    }

    // Check 3: User profile has activeTeamId
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const userData = userSnap.data();
      result.userHasActiveTeamId = userData?.activeTeamId === teamId;
    }

    // All checks passed
    result.ready = result.teamExists && result.memberExists && !!result.memberRole;

    logger.info('[SampleData] verifySeedReadiness result:', {
      teamId,
      uid,
      ...result,
    });

    return result;
  } catch (error) {
    result.error = error instanceof Error ? error.message : 'Unknown error during readiness check';
    logger.error('[SampleData] verifySeedReadiness error:', error);
    return result;
  }
}

/**
 * Wait for seed readiness with retries
 * Useful when team was just created and documents may not be propagated yet
 */
export async function waitForSeedReadiness(
  teamId: string,
  maxAttempts: number = 5,
  delayMs: number = 500
): Promise<SeedReadinessResult> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const result = await verifySeedReadiness(teamId);

    if (result.ready) {
      logger.info('[SampleData] Seed readiness confirmed on attempt', attempt);
      return result;
    }

    if (attempt < maxAttempts) {
      logger.info(`[SampleData] Waiting for seed readiness (attempt ${attempt}/${maxAttempts})...`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  // Return last result after all attempts
  const finalResult = await verifySeedReadiness(teamId);
  if (!finalResult.ready) {
    logger.error('[SampleData] Seed readiness check failed after all attempts:', finalResult);
  }
  return finalResult;
}

/**
 * Seeding configuration based on business type
 *
 * - dealer: vehicles, customers, purchase invoices, sales invoices, appointments
 * - garage: vehicles, customers, inventory (partStockLines), work orders, appointments
 * - dealer_garage: everything
 */
interface SeedingConfig {
  vehicles: boolean;
  customers: boolean;
  purchaseInvoices: boolean;
  salesInvoices: boolean;
  appointments: boolean;
  partStockLines: boolean;
  workOrders: boolean;
}

function getSeedingConfig(businessType: BusinessType | null): SeedingConfig {
  switch (businessType) {
    case 'dealer':
      return {
        vehicles: true,
        customers: true,
        purchaseInvoices: true,
        salesInvoices: true,
        appointments: true,
        partStockLines: false,
        workOrders: false,
      };
    case 'garage':
      return {
        vehicles: true,
        customers: true,
        purchaseInvoices: false,
        salesInvoices: false,
        appointments: true,
        partStockLines: true,
        workOrders: true,
      };
    case 'dealer_garage':
    default:
      // Default to everything if no businessType specified (backward compatibility)
      return {
        vehicles: true,
        customers: true,
        purchaseInvoices: true,
        salesInvoices: true,
        appointments: true,
        partStockLines: true,
        workOrders: true,
      };
  }
}

// ============================================================================
// Sample Data Configuration
// ============================================================================

/**
 * Seed run metadata stored at teams/{teamId}/system/seedRuns/{seedRunId}
 */
export interface SeedRunMetadata {
  seedRunId: string;
  createdAt: Timestamp;
  createdByUid: string;
  seedVersion: number;
  baseDate: Timestamp;
  businessType: BusinessType | null;
  status: 'active' | 'deleted';
  deletedAt?: Timestamp;
  documentCounts: {
    customers: number;
    vehicles: number;
    purchaseInvoices: number;
    salesInvoices: number;
    appointments: number;
    partStockLines: number;
    workOrders: number;
  };
}

/**
 * Common fields for all sample documents
 */
interface SampleDocumentBase {
  isSample: true;
  sampleLabel: 'SAMPLE';
  createdBySystem: true;
  seedRunId: string;
  seedCreatedAt: Timestamp;
  deletedAt: null; // Required for Firestore where("deletedAt", "==", null) queries
}

// Current seed version - increment when sample data structure changes
const _SEED_VERSION = 2;

/**
 * Generate a unique seed run ID
 */
function generateSeedRunId(): string {
  return `seed_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
}

/**
 * Generate deterministic sample document ID
 */
function sampleId(collection: string, index: number): string {
  return `sample_${collection}_${String(index).padStart(2, '0')}`;
}

/**
 * Get a date relative to a base date (future)
 */
function daysFromBase(baseDate: Date, days: number): Timestamp {
  const date = new Date(baseDate);
  date.setDate(date.getDate() + days);
  date.setHours(10, 0, 0, 0);
  return Timestamp.fromDate(date);
}

/**
 * Get a date before the base date (past)
 */
function daysBeforeBase(baseDate: Date, days: number): Timestamp {
  const date = new Date(baseDate);
  date.setDate(date.getDate() - days);
  date.setHours(10, 0, 0, 0);
  return Timestamp.fromDate(date);
}

// ============================================================================
// Sample Data Definitions
// ============================================================================

/**
 * Sample customers (5 total — canonical set, single source of truth)
 * [1] Supplier A (Jansen): 2 purchase invoices (INK-001, INK-002)
 * [2] Supplier B (De Groot): 3 purchase invoices (INK-003, INK-004, INK-005)
 * [3] Business buyer (Lease XYZ): 3 sales invoices (VERK-001, VERK-002, VERK-003)
 * [4] Private buyer 1 (Jan de Vries): 1 sales invoice (VERK-004)
 * [5] Private buyer 2 (Sophie van den Berg): 1 sales invoice (VERK-005)
 */
function getSampleCustomers(
  teamId: string,
  seedRunId: string,
  seedCreatedAt: Timestamp
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  return [
    {
      id: sampleId('customer', 1),
      teamId,
      ...base,
      type: 'company',
      companyName: 'SAMPLE — AutoBedrijf Jansen BV',
      contactPerson: 'Pieter Jansen',
      displayName: 'SAMPLE — AutoBedrijf Jansen BV',
      searchName: 'sample — autobedrijf jansen bv',
      searchTokens: generateCustomerSearchTokens({
        firstName: undefined,
        lastName: undefined,
        companyName: 'SAMPLE — AutoBedrijf Jansen BV',
        email: 'sample.jansen@example.com',
        phone: '+31201234567',
      }),
      email: 'sample.jansen@example.com',
      phone: '+31201234567',
      addressLine1: 'Industrieweg 45',
      city: 'Rotterdam',
      postalCode: '3012 CD',
      country: 'NL',
      vatNumber: 'NL123456789B01',
      status: 'customer',
      lifecycleState: 'active',
      role: 'supplier',
      isVendor: true,
      tags: ['sample', 'leverancier'],
      notes: 'Leverancier A — 2 inkoopfacturen (INK-001, INK-002).',
    },
    {
      id: sampleId('customer', 2),
      teamId,
      ...base,
      type: 'company',
      companyName: 'SAMPLE — Garage De Groot',
      contactPerson: 'Willem de Groot',
      displayName: 'SAMPLE — Garage De Groot',
      searchName: 'sample — garage de groot',
      searchTokens: generateCustomerSearchTokens({
        firstName: undefined,
        lastName: undefined,
        companyName: 'SAMPLE — Garage De Groot',
        email: 'sample.degroot@example.com',
        phone: '+31505551234',
      }),
      email: 'sample.degroot@example.com',
      phone: '+31505551234',
      addressLine1: 'Werkplaatsstraat 8',
      city: 'Groningen',
      postalCode: '9711 KL',
      country: 'NL',
      vatNumber: 'NL111222333B01',
      status: 'customer',
      lifecycleState: 'active',
      role: 'supplier',
      isVendor: true,
      tags: ['sample', 'leverancier'],
      notes: 'Leverancier B — 3 inkoopfacturen (INK-003, INK-004, INK-005).',
    },
    {
      id: sampleId('customer', 3),
      teamId,
      ...base,
      type: 'company',
      companyName: 'SAMPLE — Lease Maatschappij XYZ',
      contactPerson: 'Kees van Dam',
      displayName: 'SAMPLE — Lease Maatschappij XYZ',
      searchName: 'sample — lease maatschappij xyz',
      searchTokens: generateCustomerSearchTokens({
        firstName: undefined,
        lastName: undefined,
        companyName: 'SAMPLE — Lease Maatschappij XYZ',
        email: 'sample.lease@example.com',
        phone: '+31307654321',
      }),
      email: 'sample.lease@example.com',
      phone: '+31307654321',
      addressLine1: 'Businesspark 12',
      city: 'Den Haag',
      postalCode: '2521 GH',
      country: 'NL',
      vatNumber: 'NL987654321B01',
      status: 'customer',
      lifecycleState: 'active',
      role: 'buyer',
      isCustomer: true,
      tags: ['sample', 'zakelijk'],
      notes: 'Zakelijke koper — 3 verkoopfacturen (VERK-001, VERK-002, VERK-003).',
    },
    {
      id: sampleId('customer', 4),
      teamId,
      ...base,
      type: 'private',
      firstName: 'SAMPLE — Jan',
      lastName: 'de Vries',
      displayName: 'SAMPLE — Jan de Vries',
      searchName: 'sample — jan de vries',
      searchTokens: generateCustomerSearchTokens({
        firstName: 'SAMPLE — Jan',
        lastName: 'de Vries',
        companyName: undefined,
        email: 'sample.jan@example.com',
        phone: '+31612345678',
      }),
      email: 'sample.jan@example.com',
      phone: '+31612345678',
      addressLine1: 'Voorbeeldstraat 123',
      city: 'Amsterdam',
      postalCode: '1012 AB',
      country: 'NL',
      status: 'customer',
      lifecycleState: 'active',
      role: 'buyer',
      isCustomer: true,
      tags: ['sample'],
      notes: 'Particuliere koper — 1 verkoopfactuur (VERK-004).',
    },
    {
      id: sampleId('customer', 5),
      teamId,
      ...base,
      type: 'private',
      firstName: 'SAMPLE — Sophie',
      lastName: 'van den Berg',
      displayName: 'SAMPLE — Sophie van den Berg',
      searchName: 'sample — sophie van den berg',
      searchTokens: generateCustomerSearchTokens({
        firstName: 'SAMPLE — Sophie',
        lastName: 'van den Berg',
        companyName: undefined,
        email: 'sample.sophie@example.com',
        phone: '+31611223344',
      }),
      email: 'sample.sophie@example.com',
      phone: '+31611223344',
      addressLine1: 'Laan van Meerdervoort 200',
      city: 'Den Haag',
      postalCode: '2517 BH',
      country: 'NL',
      status: 'customer',
      lifecycleState: 'active',
      role: 'buyer',
      isCustomer: true,
      tags: ['sample'],
      notes: 'Particuliere koper — 1 verkoopfactuur (VERK-005).',
    },
  ];
}

/**
 * Sample vehicles (5 total — canonical set, all sold, Option A)
 * vehicle_01/02 purchased from Jansen (customer_01), vehicle_03/04/05 from De Groot (customer_02)
 * vehicle_01/02/03 sold to Lease XYZ (customer_03), vehicle_04 to Jan (customer_04), vehicle_05 to Sophie (customer_05)
 */
function getSampleVehicles(
  teamId: string,
  seedRunId: string,
  seedCreatedAt: Timestamp,
  baseDate: Date
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  return [
    {
      id: sampleId('vehicle', 1),
      teamId,
      ...base,
      licensePlate: 'SAMPLE-01',
      vin: 'WVWZZZ3CZWE123456',
      brand: 'Volkswagen',
      model: 'Golf',
      variant: '1.4 TSI Comfortline',
      year: 2022,
      mileage: 45000,
      fuelType: 'petrol',
      transmission: 'manual',
      color: 'Zilver',
      status: 'sold',
      purchasePrice: 1500000,
      salePrice: 1850000,
      purchaseDate: daysBeforeBase(baseDate, 60),
      currentOwnerId: sampleId('customer', 3),
      searchName: 'sample-01 volkswagen golf',
      searchTokens: generateVehicleSearchTokens({
        brand: 'Volkswagen',
        model: 'Golf',
        licensePlate: 'SAMPLE-01',
      }),
      tags: ['sample', 'verkocht'],
      notes: 'Ingekocht bij Jansen (INK-001), verkocht aan Lease XYZ (VERK-001).',
      linkedCustomerIds: [sampleId('customer', 1), sampleId('customer', 3)],
    },
    {
      id: sampleId('vehicle', 2),
      teamId,
      ...base,
      licensePlate: 'SAMPLE-02',
      vin: 'WBADT43452GZ12345',
      brand: 'BMW',
      model: '3 Serie',
      variant: '320d Business Line',
      year: 2021,
      mileage: 62000,
      fuelType: 'diesel',
      transmission: 'automatic',
      color: 'Zwart',
      status: 'sold',
      purchasePrice: 2200000,
      salePrice: 2650000,
      purchaseDate: daysBeforeBase(baseDate, 45),
      currentOwnerId: sampleId('customer', 3),
      searchName: 'sample-02 bmw 3 serie',
      searchTokens: generateVehicleSearchTokens({
        brand: 'BMW',
        model: '3 Serie',
        licensePlate: 'SAMPLE-02',
      }),
      tags: ['sample', 'verkocht'],
      notes: 'Ingekocht bij Jansen (INK-002), verkocht aan Lease XYZ (VERK-002).',
      linkedCustomerIds: [sampleId('customer', 1), sampleId('customer', 3)],
    },
    {
      id: sampleId('vehicle', 3),
      teamId,
      ...base,
      licensePlate: 'SAMPLE-03',
      vin: 'TMBEF25J5C2123456',
      brand: 'Skoda',
      model: 'Octavia',
      variant: '1.5 TSI Ambition',
      year: 2023,
      mileage: 28000,
      fuelType: 'petrol',
      transmission: 'manual',
      color: 'Wit',
      status: 'sold',
      purchasePrice: 2400000,
      salePrice: 2850000,
      purchaseDate: daysBeforeBase(baseDate, 30),
      currentOwnerId: sampleId('customer', 3),
      searchName: 'sample-03 skoda octavia',
      searchTokens: generateVehicleSearchTokens({
        brand: 'Skoda',
        model: 'Octavia',
        licensePlate: 'SAMPLE-03',
      }),
      tags: ['sample', 'verkocht'],
      notes: 'Ingekocht bij De Groot (INK-003), verkocht aan Lease XYZ (VERK-003).',
      linkedCustomerIds: [sampleId('customer', 2), sampleId('customer', 3)],
    },
    {
      id: sampleId('vehicle', 4),
      teamId,
      ...base,
      licensePlate: 'SAMPLE-04',
      vin: 'WF0XXXGAJXDA12345',
      brand: 'Ford',
      model: 'Focus',
      variant: '1.0 EcoBoost Titanium',
      year: 2020,
      mileage: 78000,
      fuelType: 'petrol',
      transmission: 'manual',
      color: 'Blauw',
      status: 'sold',
      purchasePrice: 1100000,
      salePrice: 1450000,
      purchaseDate: daysBeforeBase(baseDate, 20),
      currentOwnerId: sampleId('customer', 4),
      searchName: 'sample-04 ford focus',
      searchTokens: generateVehicleSearchTokens({
        brand: 'Ford',
        model: 'Focus',
        licensePlate: 'SAMPLE-04',
      }),
      tags: ['sample', 'verkocht'],
      notes: 'Ingekocht bij De Groot (INK-004), verkocht aan Jan de Vries (VERK-004).',
      linkedCustomerIds: [sampleId('customer', 2), sampleId('customer', 4)],
    },
    {
      id: sampleId('vehicle', 5),
      teamId,
      ...base,
      licensePlate: 'SAMPLE-05',
      vin: 'WAUZZZ8V8DA123456',
      brand: 'Audi',
      model: 'A3',
      variant: '1.4 TFSI Sport',
      year: 2023,
      mileage: 15000,
      fuelType: 'petrol',
      transmission: 'automatic',
      color: 'Grijs',
      status: 'sold',
      purchasePrice: 2800000,
      salePrice: 3250000,
      purchaseDate: daysBeforeBase(baseDate, 14),
      currentOwnerId: sampleId('customer', 5),
      searchName: 'sample-05 audi a3',
      searchTokens: generateVehicleSearchTokens({
        brand: 'Audi',
        model: 'A3',
        licensePlate: 'SAMPLE-05',
      }),
      tags: ['sample', 'verkocht'],
      notes: 'Ingekocht bij De Groot (INK-005), verkocht aan Sophie van den Berg (VERK-005).',
      linkedCustomerIds: [sampleId('customer', 2), sampleId('customer', 5)],
    },
  ];
}

/**
 * Sample purchase invoices (5 total)
 * Linked to vehicles and suppliers
 * ALL invoices use status: 'concept' to ensure they can be deleted
 */
function getSamplePurchaseInvoices(
  teamId: string,
  seedRunId: string,
  seedCreatedAt: Timestamp,
  baseDate: Date
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  return [
    {
      id: sampleId('purchase', 1),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-INK-001',
      invoiceDate: daysBeforeBase(baseDate, 60),
      dueDate: daysBeforeBase(baseDate, 30),
      partyId: sampleId('customer', 1),
      partyName: 'SAMPLE — AutoBedrijf Jansen BV',
      taxType: 'margin',
      vatRate: null,
      status: 'concept', // MUST be concept to allow deletion
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 1),
          description: 'SAMPLE — Volkswagen Golf 1.4 TSI',
          quantity: 1,
          unitPriceCents: 1500000,
          vatRate: 0,
          totalCents: 1500000,
        },
      ],
      subtotalCents: 1500000,
      vatCents: 0,
      totalCents: 1500000,
      primaryVehicleId: sampleId('vehicle', 1),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-01'],
      vehicleIds: [sampleId('vehicle', 1)],
      hasTradeIn: false,
      ...generatePurchaseInvoiceSearchFields({
        invoiceNo: 'SAMPLE-INK-001',
        supplierName: 'SAMPLE — AutoBedrijf Jansen BV',
        vehiclePlates: ['SAMPLE-01'],
      }),
      notes: 'Voorbeeldfactuur inkoop.',
      tags: ['sample'],
    },
    {
      id: sampleId('purchase', 2),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-INK-002',
      invoiceDate: daysBeforeBase(baseDate, 45),
      dueDate: daysBeforeBase(baseDate, 15),
      partyId: sampleId('customer', 1),
      partyName: 'SAMPLE — AutoBedrijf Jansen BV',
      taxType: 'vat',
      vatRate: 21,
      status: 'concept', // MUST be concept to allow deletion
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 2),
          description: 'SAMPLE — BMW 320i Executive',
          quantity: 1,
          unitPriceCents: 2200000,
          vatRate: 21,
          totalCents: 2662000,
        },
      ],
      subtotalCents: 2200000,
      vatCents: 462000,
      totalCents: 2662000,
      primaryVehicleId: sampleId('vehicle', 2),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-02'],
      vehicleIds: [sampleId('vehicle', 2)],
      hasTradeIn: false,
      ...generatePurchaseInvoiceSearchFields({
        invoiceNo: 'SAMPLE-INK-002',
        supplierName: 'SAMPLE — AutoBedrijf Jansen BV',
        vehiclePlates: ['SAMPLE-02'],
      }),
      notes: 'Inkoop met BTW.',
      tags: ['sample'],
    },
    {
      id: sampleId('purchase', 3),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-INK-003',
      invoiceDate: daysBeforeBase(baseDate, 30),
      dueDate: daysBeforeBase(baseDate, 0),
      partyId: sampleId('customer', 2),
      partyName: 'SAMPLE — Garage De Groot',
      taxType: 'margin',
      vatRate: null,
      status: 'concept', // MUST be concept to allow deletion
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 3),
          description: 'SAMPLE — Skoda Octavia 1.5 TSI',
          quantity: 1,
          unitPriceCents: 2400000,
          vatRate: 0,
          totalCents: 2400000,
        },
      ],
      subtotalCents: 2400000,
      vatCents: 0,
      totalCents: 2400000,
      primaryVehicleId: sampleId('vehicle', 3),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-03'],
      vehicleIds: [sampleId('vehicle', 3)],
      hasTradeIn: false,
      ...generatePurchaseInvoiceSearchFields({
        invoiceNo: 'SAMPLE-INK-003',
        supplierName: 'SAMPLE — Garage De Groot',
        vehiclePlates: ['SAMPLE-03'],
      }),
      notes: 'Openstaande factuur.',
      tags: ['sample'],
    },
    {
      id: sampleId('purchase', 4),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-INK-004',
      invoiceDate: daysBeforeBase(baseDate, 20),
      dueDate: daysFromBase(baseDate, 10),
      partyId: sampleId('customer', 2),
      partyName: 'SAMPLE — Garage De Groot',
      taxType: 'margin',
      vatRate: null,
      status: 'concept', // MUST be concept to allow deletion
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 4),
          description: 'SAMPLE — Ford Focus EcoBoost',
          quantity: 1,
          unitPriceCents: 1100000,
          vatRate: 0,
          totalCents: 1100000,
        },
      ],
      subtotalCents: 1100000,
      vatCents: 0,
      totalCents: 1100000,
      primaryVehicleId: sampleId('vehicle', 4),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-04'],
      vehicleIds: [sampleId('vehicle', 4)],
      hasTradeIn: false,
      ...generatePurchaseInvoiceSearchFields({
        invoiceNo: 'SAMPLE-INK-004',
        supplierName: 'SAMPLE — Garage De Groot',
        vehiclePlates: ['SAMPLE-04'],
      }),
      tags: ['sample'],
    },
    {
      id: sampleId('purchase', 5),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-INK-005',
      invoiceDate: daysBeforeBase(baseDate, 14),
      dueDate: daysFromBase(baseDate, 16),
      partyId: sampleId('customer', 2),
      partyName: 'SAMPLE — Garage De Groot',
      taxType: 'margin',
      vatRate: null,
      status: 'concept', // MUST be concept to allow deletion
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 5),
          description: 'SAMPLE — Audi A3 35 TFSI',
          quantity: 1,
          unitPriceCents: 2800000,
          vatRate: 0,
          totalCents: 2800000,
        },
      ],
      subtotalCents: 2800000,
      vatCents: 0,
      totalCents: 2800000,
      primaryVehicleId: sampleId('vehicle', 5),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-05'],
      vehicleIds: [sampleId('vehicle', 5)],
      hasTradeIn: false,
      ...generatePurchaseInvoiceSearchFields({
        invoiceNo: 'SAMPLE-INK-005',
        supplierName: 'SAMPLE — Garage De Groot',
        vehiclePlates: ['SAMPLE-05'],
      }),
      tags: ['sample'],
    },
  ];
}

/**
 * Sample sales invoices (5 total — canonical set)
 * VERK-001/002/003 → Lease XYZ (customer_03, business buyer)
 * VERK-004 → Jan de Vries (customer_04, private buyer 1)
 * VERK-005 → Sophie van den Berg (customer_05, private buyer 2)
 * ALL invoices use status: 'concept' to ensure they can be deleted
 */
function getSampleSalesInvoices(
  teamId: string,
  seedRunId: string,
  seedCreatedAt: Timestamp,
  baseDate: Date
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  const sampleWarrantyTerms = {
    guarantee: {
      title: 'SAMPLE — Garantievoorwaarden',
      body: 'Dit is een voorbeeldgarantie. Bij een echte factuur vult u hier uw eigen garantievoorwaarden in.',
    },
  };

  return [
    {
      id: sampleId('sales', 1),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-VERK-001',
      invoiceDate: daysBeforeBase(baseDate, 50),
      dueDate: daysBeforeBase(baseDate, 20),
      partyId: sampleId('customer', 3),
      partyName: 'SAMPLE — Lease Maatschappij XYZ',
      taxType: 'margin',
      vatRate: null,
      status: 'concept',
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 1),
          description: 'SAMPLE — Volkswagen Golf 1.4 TSI Comfortline',
          quantity: 1,
          unitPriceCents: 1850000,
          vatRate: 0,
          totalCents: 1850000,
        },
      ],
      subtotalCents: 1850000,
      vatCents: 0,
      totalCents: 1850000,
      primaryVehicleId: sampleId('vehicle', 1),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-01'],
      vehicleIds: [sampleId('vehicle', 1)],
      hasTradeIn: false,
      ...generateSalesInvoiceSearchFields({
        invoiceNo: 'SAMPLE-VERK-001',
        customerName: 'SAMPLE — Lease Maatschappij XYZ',
        companyName: 'SAMPLE — Lease Maatschappij XYZ',
        vehiclePlates: ['SAMPLE-01'],
      }),
      notes: 'Verkoop VW Golf aan Lease Maatschappij XYZ.',
      tags: ['sample'],
      terms: sampleWarrantyTerms,
    },
    {
      id: sampleId('sales', 2),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-VERK-002',
      invoiceDate: daysBeforeBase(baseDate, 35),
      dueDate: daysBeforeBase(baseDate, 5),
      partyId: sampleId('customer', 3),
      partyName: 'SAMPLE — Lease Maatschappij XYZ',
      taxType: 'vat',
      vatRate: 21,
      status: 'concept',
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 2),
          description: 'SAMPLE — BMW 3 Serie 320i Executive',
          quantity: 1,
          unitPriceCents: 2190083,
          vatRate: 21,
          totalCents: 2650000,
        },
      ],
      subtotalCents: 2190083,
      vatCents: 459917,
      totalCents: 2650000,
      primaryVehicleId: sampleId('vehicle', 2),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-02'],
      vehicleIds: [sampleId('vehicle', 2)],
      hasTradeIn: false,
      notes: 'Verkoop BMW aan Lease Maatschappij XYZ, met BTW.',
      tags: ['sample'],
      terms: sampleWarrantyTerms,
    },
    {
      id: sampleId('sales', 3),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-VERK-003',
      invoiceDate: daysBeforeBase(baseDate, 20),
      dueDate: daysFromBase(baseDate, 10),
      partyId: sampleId('customer', 3),
      partyName: 'SAMPLE — Lease Maatschappij XYZ',
      taxType: 'margin',
      vatRate: null,
      status: 'concept',
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 3),
          description: 'SAMPLE — Skoda Octavia 1.5 TSI Style',
          quantity: 1,
          unitPriceCents: 2850000,
          vatRate: 0,
          totalCents: 2850000,
        },
      ],
      subtotalCents: 2850000,
      vatCents: 0,
      totalCents: 2850000,
      primaryVehicleId: sampleId('vehicle', 3),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-03'],
      vehicleIds: [sampleId('vehicle', 3)],
      hasTradeIn: false,
      ...generateSalesInvoiceSearchFields({
        invoiceNo: 'SAMPLE-VERK-003',
        customerName: 'SAMPLE — Lease Maatschappij XYZ',
        companyName: 'SAMPLE — Lease Maatschappij XYZ',
        vehiclePlates: ['SAMPLE-03'],
      }),
      notes: 'Verkoop Skoda Octavia aan Lease Maatschappij XYZ.',
      tags: ['sample'],
      terms: sampleWarrantyTerms,
    },
    {
      id: sampleId('sales', 4),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-VERK-004',
      invoiceDate: daysBeforeBase(baseDate, 10),
      dueDate: daysFromBase(baseDate, 20),
      partyId: sampleId('customer', 4),
      partyName: 'SAMPLE — Jan de Vries',
      taxType: 'margin',
      vatRate: null,
      status: 'concept',
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 4),
          description: 'SAMPLE — Ford Focus 1.0 EcoBoost Titanium',
          quantity: 1,
          unitPriceCents: 1450000,
          vatRate: 0,
          totalCents: 1450000,
        },
      ],
      subtotalCents: 1450000,
      vatCents: 0,
      totalCents: 1450000,
      primaryVehicleId: sampleId('vehicle', 4),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-04'],
      vehicleIds: [sampleId('vehicle', 4)],
      hasTradeIn: false,
      ...generateSalesInvoiceSearchFields({
        invoiceNo: 'SAMPLE-VERK-004',
        customerName: 'SAMPLE — Jan de Vries',
        companyName: undefined,
        vehiclePlates: ['SAMPLE-04'],
      }),
      notes: 'Verkoop Ford Focus aan particulier.',
      tags: ['sample'],
      terms: sampleWarrantyTerms,
    },
    {
      id: sampleId('sales', 5),
      teamId,
      ...base,
      invoiceNo: 'SAMPLE-VERK-005',
      invoiceDate: daysBeforeBase(baseDate, 5),
      dueDate: daysFromBase(baseDate, 25),
      partyId: sampleId('customer', 5),
      partyName: 'SAMPLE — Sophie van den Berg',
      taxType: 'margin',
      vatRate: null,
      status: 'concept',
      lineItems: [
        {
          id: 'line_1',
          type: 'vehicle',
          vehicleId: sampleId('vehicle', 5),
          description: 'SAMPLE — Audi A3 35 TFSI S-Line',
          quantity: 1,
          unitPriceCents: 3250000,
          vatRate: 0,
          totalCents: 3250000,
        },
      ],
      subtotalCents: 3250000,
      vatCents: 0,
      totalCents: 3250000,
      primaryVehicleId: sampleId('vehicle', 5),
      vehicleCount: 1,
      vehiclePlates: ['SAMPLE-05'],
      vehicleIds: [sampleId('vehicle', 5)],
      hasTradeIn: false,
      ...generateSalesInvoiceSearchFields({
        invoiceNo: 'SAMPLE-VERK-005',
        customerName: 'SAMPLE — Sophie van den Berg',
        companyName: undefined,
        vehiclePlates: ['SAMPLE-05'],
      }),
      notes: 'Verkoop Audi A3 aan particulier.',
      tags: ['sample'],
      terms: sampleWarrantyTerms,
    },
  ];
}

/**
 * Sample part stock lines (3 total — canonical set)
 * All linked to purchase invoice INK-001 (Jansen, customer_01)
 */
function getSamplePartStockLines(
  teamId: string,
  seedRunId: string,
  seedCreatedAt: Timestamp,
  baseDate: Date
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  return [
    {
      id: sampleId('stockline', 1),
      teamId,
      ...base,
      sourcePurchaseInvoiceId: sampleId('purchase', 1),
      supplierId: sampleId('customer', 1),
      supplierName: 'SAMPLE — AutoBedrijf Jansen BV',
      invoiceNumber: 'SAMPLE-INK-001',
      invoiceDate: daysBeforeBase(baseDate, 14).toDate().toISOString().split('T')[0],
      partName: 'SAMPLE — Remblokken voor (set)',
      unit: 'set',
      category: 'brakes',
      oemNumber: 'BP-VW-001',
      oemNumberNormalized: 'BPVW001',
      sku: 'SAMPLE-REM-001',
      skuNormalized: 'SAMPLEREM001',
      partName_lower: 'sample — remblokken voor (set)',
      vatRate: 21,
      quantityPurchased: 4,
      quantityAvailable: 3,
      quantityReserved: 0,
      quantityConsumed: 1,
      unitCostCents: 4500, // €45.00
      totalCostCents: 18000, // €180.00
      currency: 'EUR',
      createdVia: 'seed',
    },
    {
      id: sampleId('stockline', 2),
      teamId,
      ...base,
      sourcePurchaseInvoiceId: sampleId('purchase', 1),
      supplierId: sampleId('customer', 1),
      supplierName: 'SAMPLE — AutoBedrijf Jansen BV',
      invoiceNumber: 'SAMPLE-INK-001',
      invoiceDate: daysBeforeBase(baseDate, 60).toDate().toISOString().split('T')[0],
      partName: 'SAMPLE — Oliefilter',
      unit: 'piece',
      category: 'filters',
      oemNumber: 'OF-UNI-001',
      oemNumberNormalized: 'OFUNI001',
      sku: 'SAMPLE-FIL-001',
      skuNormalized: 'SAMPLEFIL001',
      partName_lower: 'sample — oliefilter',
      vatRate: 21,
      quantityPurchased: 10,
      quantityAvailable: 8,
      quantityReserved: 0,
      quantityConsumed: 2,
      unitCostCents: 1200, // €12.00
      totalCostCents: 12000, // €120.00
      currency: 'EUR',
      createdVia: 'seed',
    },
    {
      id: sampleId('stockline', 3),
      teamId,
      ...base,
      sourcePurchaseInvoiceId: sampleId('purchase', 1),
      supplierId: sampleId('customer', 1),
      supplierName: 'SAMPLE — AutoBedrijf Jansen BV',
      invoiceNumber: 'SAMPLE-INK-001',
      invoiceDate: daysBeforeBase(baseDate, 60).toDate().toISOString().split('T')[0],
      partName: 'SAMPLE — Motorolie 5W-30 (5L)',
      unit: 'liter',
      category: 'oil',
      oemNumber: 'OIL-5W30-5L',
      oemNumberNormalized: 'OIL5W305L',
      sku: 'SAMPLE-OIL-001',
      skuNormalized: 'SAMPLEOIL001',
      partName_lower: 'sample — motorolie 5w-30 (5l)',
      vatRate: 21,
      quantityPurchased: 20,
      quantityAvailable: 15,
      quantityReserved: 0,
      quantityConsumed: 5,
      unitCostCents: 800, // €8.00 per liter
      totalCostCents: 16000, // €160.00
      currency: 'EUR',
      lowStockThreshold: 5,
      createdVia: 'seed',
    },
  ];
}

/**
 * Sample work orders (3 total — canonical set)
 * WO-1: vehicle_01 + customer_04 (Jan de Vries)
 * WO-2: vehicle_05 + no customer
 * WO-3: vehicle_02 + customer_03 (Lease XYZ)
 */
function getSampleWorkOrders(
  teamId: string,
  createdBy: string,
  seedRunId: string,
  seedCreatedAt: Timestamp,
  baseDate: Date
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  const now = baseDate.toISOString();

  return [
    {
      id: sampleId('workorder', 1),
      teamId,
      ...base,
      vehicleId: sampleId('vehicle', 1),
      customerId: sampleId('customer', 4),
      title: 'SAMPLE — Grote beurt + APK',
      status: 'DONE',
      type: 'ONDERHOUD',
      priority: 'NORMAAL',
      plannedStart: daysBeforeBase(baseDate, 5).toDate().toISOString(),
      plannedEnd: daysBeforeBase(baseDate, 5).toDate().toISOString(),
      notes: 'Voorbeeldwerkorder - olieverversing, filters vervangen, APK keuring uitgevoerd.',
      totalPrice: 24900, // €249.00
      hourlyRate: 6500, // €65.00
      hours: 2.5,
      totals: {
        hoursExcl: 16250,
        partsExcl: 6500,
        otherExcl: 0,
        vatExcl: 4778,
        totalExcl: 22750,
        totalIncl: 27528,
      },
      createdBy,
      createdAt: daysBeforeBase(baseDate, 7).toDate().toISOString(),
      updatedAt: daysBeforeBase(baseDate, 5).toDate().toISOString(),
    },
    {
      id: sampleId('workorder', 2),
      teamId,
      ...base,
      vehicleId: sampleId('vehicle', 5), // Audi A3 (in repair)
      customerId: null,
      title: 'SAMPLE — APK keuring',
      status: 'IN_PROGRESS',
      type: 'DIAGNOSE',
      priority: 'HOOG',
      plannedStart: daysFromBase(baseDate, 0).toDate().toISOString(),
      plannedEnd: daysFromBase(baseDate, 0).toDate().toISOString(),
      notes: 'Voorbeeldwerkorder - APK keuring voor verkoop.',
      totalPrice: null,
      hourlyRate: 6500,
      hours: null,
      createdBy,
      createdAt: daysBeforeBase(baseDate, 2).toDate().toISOString(),
      updatedAt: now,
    },
    {
      id: sampleId('workorder', 3),
      teamId,
      ...base,
      vehicleId: sampleId('vehicle', 2),
      customerId: sampleId('customer', 3),
      title: 'SAMPLE — Bandenwissel zomer',
      status: 'PLANNED',
      type: 'ONDERHOUD',
      priority: 'NORMAAL',
      plannedStart: daysFromBase(baseDate, 3).toDate().toISOString(),
      plannedEnd: daysFromBase(baseDate, 3).toDate().toISOString(),
      notes: 'Voorbeeldwerkorder - zomerbanden monteren voor klant.',
      totalPrice: null,
      hourlyRate: 6500,
      hours: null,
      createdBy,
      createdAt: daysBeforeBase(baseDate, 1).toDate().toISOString(),
      updatedAt: now,
    },
  ];
}

/**
 * Sample appointments (3 total — canonical set)
 * Appt-1: Jan de Vries (customer_04) + BMW (vehicle_02)
 * Appt-2: APK task for Audi (vehicle_05), no customer
 * Appt-3: Jan de Vries (customer_04) + Ford Focus (vehicle_04) delivery
 */
function getSampleAppointments(
  teamId: string,
  seedRunId: string,
  seedCreatedAt: Timestamp,
  baseDate: Date
): Array<Record<string, unknown>> {
  const base: SampleDocumentBase = {
    isSample: true,
    sampleLabel: 'SAMPLE',
    createdBySystem: true,
    seedRunId,
    seedCreatedAt,
    deletedAt: null,
  };

  return [
    {
      id: sampleId('appointment', 1),
      teamId,
      ...base,
      title: 'SAMPLE — Proefrit BMW met Jan de Vries',
      description: 'Proefrit gepland voor de BMW 320i.',
      location: 'Showroom',
      start: daysFromBase(baseDate, 2),
      end: Timestamp.fromDate(
        new Date(daysFromBase(baseDate, 2).toDate().getTime() + 60 * 60 * 1000)
      ),
      allDay: false,
      customerId: sampleId('customer', 4),
      vehicleId: sampleId('vehicle', 2),
      status: 'scheduled',
      type: 'appointment',
      tags: ['sample', 'proefrit'],
    },
    {
      id: sampleId('appointment', 2),
      teamId,
      ...base,
      title: 'SAMPLE — APK keuring Audi A3',
      description: 'APK keuring en kleine reparaties.',
      location: 'Werkplaats',
      start: daysFromBase(baseDate, 3),
      end: Timestamp.fromDate(
        new Date(daysFromBase(baseDate, 3).toDate().getTime() + 4 * 60 * 60 * 1000)
      ),
      allDay: false,
      vehicleId: sampleId('vehicle', 5),
      status: 'scheduled',
      type: 'task',
      tags: ['sample', 'apk'],
    },
    {
      id: sampleId('appointment', 3),
      teamId,
      ...base,
      title: 'SAMPLE — Aflevering Ford Focus',
      description: 'Aflevering aan Jan de Vries.',
      location: 'Showroom',
      start: daysFromBase(baseDate, 5),
      end: Timestamp.fromDate(
        new Date(daysFromBase(baseDate, 5).toDate().getTime() + 2 * 60 * 60 * 1000)
      ),
      allDay: false,
      customerId: sampleId('customer', 4),
      vehicleId: sampleId('vehicle', 4),
      status: 'confirmed',
      type: 'appointment',
      tags: ['sample', 'aflevering'],
    },
  ];
}

// ============================================================================
// Seeding Functions
// ============================================================================

/**
 * Check if team already has sample data seeded
 */
export async function hasSampleDataSeeded(teamId: string): Promise<boolean> {
  try {
    const teamRef = doc(db, 'teams', teamId);
    const teamDoc = await getDoc(teamRef);

    if (!teamDoc.exists()) {
      logger.warn('[SampleData] Team document not found:', teamId);
      return false;
    }

    const data = teamDoc.data();
    return data?.hasSampleData === true;
  } catch (error) {
    logger.error('[SampleData] Error checking hasSampleData:', error);
    return false;
  }
}

/**
 * Seed sample data for a new team
 *
 * This function is idempotent - it will only seed data once per team.
 * The hasSampleData flag on the team document prevents re-seeding.
 *
 * @param teamId - The team ID to seed data for
 * @param createdBy - The user ID who created the team (for audit fields)
 * @param businessType - The business type selected during onboarding (determines what data to seed)
 * @returns true if data was seeded, false if already seeded or error
 */
export async function seedSampleData(
  teamId: string,
  createdBy: string,
  businessType: BusinessType | null = null
): Promise<boolean> {
  try {
    // Step 0: Verify seed readiness (prevents race conditions with team/member creation)
    // This is critical for OAuth users where team creation just happened
    logger.info('[SampleData] Verifying seed readiness for team:', teamId);
    const readiness = await waitForSeedReadiness(teamId, 5, 500);

    if (!readiness.ready) {
      logger.error('[SampleData] Seed readiness check failed:', {
        teamId,
        createdBy,
        ...readiness,
      });
      throw new Error(
        `Cannot seed: ${readiness.error || 'Unknown readiness failure'}. ` +
          `Team exists: ${readiness.teamExists}, Member exists: ${readiness.memberExists}, ` +
          `Role: ${readiness.memberRole}, UID: ${readiness.currentUid}`
      );
    }

    // Step 1: Check if already seeded
    const alreadySeeded = await hasSampleDataSeeded(teamId);
    if (alreadySeeded) {
      logger.info('[SampleData] Team already has sample data, skipping:', teamId);
      return false;
    }

    logger.info(
      '[SampleData] Seeding sample data for team:',
      teamId,
      'businessType:',
      businessType
    );

    // Step 2: Get seeding configuration based on business type
    const config = getSeedingConfig(businessType);
    logger.info('[SampleData] Seeding config:', config);

    // Step 2.5: Generate seedRunId and determine baseDate
    // baseDate is used to make all sample data dates relative to team creation
    const seedRunId = generateSeedRunId();
    const seedCreatedAt = Timestamp.now();

    // Get team.createdAt as baseDate, fallback to now
    const teamRefForBaseDate = doc(db, 'teams', teamId);
    const teamDocForBaseDate = await getDoc(teamRefForBaseDate);
    const teamData = teamDocForBaseDate.data();
    const baseDate = teamData?.createdAt?.toDate?.() || new Date();

    logger.info('[SampleData] Seed run:', { seedRunId, baseDate: baseDate.toISOString() });

    // Step 3: Prepare sample documents based on config
    const customers = config.customers ? getSampleCustomers(teamId, seedRunId, seedCreatedAt) : [];
    const vehicles = config.vehicles
      ? getSampleVehicles(teamId, seedRunId, seedCreatedAt, baseDate)
      : [];
    const purchaseInvoices = config.purchaseInvoices
      ? getSamplePurchaseInvoices(teamId, seedRunId, seedCreatedAt, baseDate)
      : [];
    const salesInvoices = config.salesInvoices
      ? getSampleSalesInvoices(teamId, seedRunId, seedCreatedAt, baseDate)
      : [];
    const appointments = config.appointments
      ? getSampleAppointments(teamId, seedRunId, seedCreatedAt, baseDate)
      : [];
    const partStockLines = config.partStockLines
      ? getSamplePartStockLines(teamId, seedRunId, seedCreatedAt, baseDate)
      : [];
    const workOrders = config.workOrders
      ? getSampleWorkOrders(teamId, createdBy, seedRunId, seedCreatedAt, baseDate)
      : [];

    // Step 4: Write all documents in batches (Firestore limit: 500 per batch)
    const batch = writeBatch(db);
    const now = serverTimestamp();

    // Add customers (if any)
    // CRITICAL: Explicitly set deletedAt: null to prevent Firestore from stripping it
    for (const customer of customers) {
      const ref = doc(db, 'teams', teamId, 'customers', customer.id as string);
      batch.set(ref, {
        ...customer,
        deletedAt: null, // Required for soft-delete queries (where deletedAt == null)
        createdAt: now,
        updatedAt: now,
        createdBy,
        updatedBy: createdBy,
      });
    }

    // Add vehicles
    // CRITICAL: Explicitly set deletedAt: null to prevent Firestore from stripping it
    for (const vehicle of vehicles) {
      const ref = doc(db, 'teams', teamId, 'vehicles', vehicle.id as string);
      batch.set(ref, {
        ...vehicle,
        deletedAt: null, // Required for soft-delete queries (where deletedAt == null)
        createdAt: now,
        updatedAt: now,
        createdVia: 'seed',
        source: 'purchase',
      });
    }

    // Add purchase invoices
    // CRITICAL: Explicitly set deletedAt: null to prevent Firestore from stripping it
    for (const invoice of purchaseInvoices) {
      const ref = doc(db, 'teams', teamId, 'purchaseInvoices', invoice.id as string);
      batch.set(ref, {
        ...invoice,
        deletedAt: null, // Required for soft-delete queries (where deletedAt == null)
        createdAt: now,
        updatedAt: now,
        createdBy,
        updatedBy: createdBy,
      });
    }

    // Add sales invoices
    // CRITICAL: Explicitly set deletedAt: null to prevent Firestore from stripping it
    for (const invoice of salesInvoices) {
      const ref = doc(db, 'teams', teamId, 'salesInvoices', invoice.id as string);
      batch.set(ref, {
        ...invoice,
        deletedAt: null, // Required for soft-delete queries (where deletedAt == null)
        createdAt: now,
        updatedAt: now,
        createdBy,
        updatedBy: createdBy,
      });
    }

    // Add appointments
    for (const appointment of appointments) {
      const ref = doc(db, 'teams', teamId, 'appointments', appointment.id as string);
      batch.set(ref, {
        ...appointment,
        createdAt: now,
        updatedAt: now,
      });
    }

    // Add part stock lines (inventory)
    for (const stockLine of partStockLines) {
      const ref = doc(db, 'teams', teamId, 'partStockLines', stockLine.id as string);
      batch.set(ref, {
        ...stockLine,
        createdAt: now,
        updatedAt: now,
        createdBy,
        updatedBy: createdBy,
      });
    }

    // Add work orders (garage)
    // IMPORTANT: Override createdAt/updatedAt with serverTimestamp() to satisfy Firestore rules
    // The rules require these to be timestamps, not ISO strings
    for (const workOrder of workOrders) {
      const ref = doc(db, 'teams', teamId, 'workOrders', workOrder.id as string);
      batch.set(ref, {
        ...workOrder,
        createdAt: now,
        updatedAt: now,
      });
    }

    // Commit all sample data
    await batch.commit();

    // Step 5: Mark team as having sample data (ONLY after successful writes)
    const teamRef = doc(db, 'teams', teamId);
    await updateDoc(teamRef, {
      hasSampleData: true,
      sampleDataSeededAt: serverTimestamp(),
      sampleDataBusinessType: businessType, // Track which businessType was used for seeding
    });

    logger.info('[SampleData] Successfully seeded sample data:', {
      teamId,
      customers: customers.length,
      vehicles: vehicles.length,
      purchaseInvoices: purchaseInvoices.length,
      salesInvoices: salesInvoices.length,
      appointments: appointments.length,
      partStockLines: partStockLines.length,
      workOrders: workOrders.length,
    });

    return true;
  } catch (error) {
    // Enhanced error logging for debugging permission issues
    const errorDetails: Record<string, unknown> = {
      teamId,
      createdBy,
      businessType,
    };

    if (error instanceof Error) {
      errorDetails.message = error.message;
      errorDetails.name = error.name;

      // Check for Firestore permission error
      if (error.message.includes('permission') || error.message.includes('PERMISSION_DENIED')) {
        errorDetails.type = 'PERMISSION_ERROR';
        errorDetails.hint = 'Check if member document exists and has correct role';

        // Try to get current auth state for debugging
        const currentUser = auth.currentUser;
        errorDetails.currentUid = currentUser?.uid || 'NO_USER';
        errorDetails.currentEmail = currentUser?.email || 'NO_EMAIL';
        errorDetails.providerData = currentUser?.providerData?.map((p) => p.providerId) || [];
      }
    }

    logger.error('[SampleData] Failed to seed sample data:', errorDetails, error);
    // Do NOT set hasSampleData on failure - allow retry
    return false;
  }
}

/**
 * Delete sample data result with counts per collection
 */
export interface DeleteSampleDataResult {
  totalDeleted: number;
  deleted: {
    customers: number;
    vehicles: number;
    purchaseInvoices: number;
    salesInvoices: number;
    appointments: number;
    partStockLines: number;
    workOrders: number;
  };
}

/**
 * Delete all sample data for a team
 *
 * This function queries all documents where isSample === true and deletes them.
 * It handles Firestore batch limits (500 per batch) by chunking.
 * It also sets hasSampleData = false to reflect the current state.
 *
 * @param teamId - The team ID to delete sample data for
 * @returns Detailed result with counts per collection
 */
export async function deleteSampleData(teamId: string): Promise<DeleteSampleDataResult> {
  try {
    logger.info('[SampleData] Deleting sample data for team:', teamId);

    // Collections that may contain sample data
    const SAMPLE_COLLECTIONS = [
      'customers',
      'vehicles',
      'purchaseInvoices',
      'salesInvoices',
      'appointments',
      'partStockLines',
      'workOrders',
    ] as const;

    // Collection configs with their subcollections
    const collectionConfigs: Array<{
      name: (typeof SAMPLE_COLLECTIONS)[number];
      subcollections: string[];
    }> = [
      { name: 'customers', subcollections: ['events'] },
      { name: 'vehicles', subcollections: ['events'] },
      { name: 'purchaseInvoices', subcollections: ['assignments', 'events'] },
      { name: 'salesInvoices', subcollections: ['assignments', 'events'] },
      { name: 'appointments', subcollections: [] },
      { name: 'partStockLines', subcollections: ['events'] },
      { name: 'workOrders', subcollections: [] },
    ];

    const result: DeleteSampleDataResult = {
      totalDeleted: 0,
      deleted: {
        customers: 0,
        vehicles: 0,
        purchaseInvoices: 0,
        salesInvoices: 0,
        appointments: 0,
        partStockLines: 0,
        workOrders: 0,
      },
    };

    const BATCH_SIZE = 500;

    // Process each collection
    for (const config of collectionConfigs) {
      const collectionRef = collection(db, 'teams', teamId, config.name);
      const sampleQuery = query(collectionRef, where('isSample', '==', true));
      const snapshot = await getDocs(sampleQuery);

      if (snapshot.empty) {
        logger.info(`[SampleData] No sample data in ${config.name}`);
        continue;
      }

      // First, delete subcollections for each sample document
      for (const docSnapshot of snapshot.docs) {
        for (const subcollectionName of config.subcollections) {
          const subcollectionRef = collection(docSnapshot.ref, subcollectionName);
          const subcollectionSnapshot = await getDocs(subcollectionRef);

          if (!subcollectionSnapshot.empty) {
            // Delete subcollection docs in batches
            const subDocs = subcollectionSnapshot.docs;
            for (let i = 0; i < subDocs.length; i += BATCH_SIZE) {
              const batch = writeBatch(db);
              const chunk = subDocs.slice(i, i + BATCH_SIZE);

              for (const subDoc of chunk) {
                batch.delete(subDoc.ref);
              }

              await batch.commit();
              logger.info(
                `[SampleData] Deleted ${chunk.length} from ${config.name}/${docSnapshot.id}/${subcollectionName}`
              );
            }
          }
        }
      }

      // Then delete the parent documents in batches
      const docs = snapshot.docs;

      for (let i = 0; i < docs.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = docs.slice(i, i + BATCH_SIZE);

        for (const docSnapshot of chunk) {
          batch.delete(docSnapshot.ref);
        }

        await batch.commit();
        result.deleted[config.name] += chunk.length;
        result.totalDeleted += chunk.length;

        logger.info(`[SampleData] Deleted ${chunk.length} from ${config.name}`);
      }
    }

    // Update team document to reflect sample data is gone
    const teamRef = doc(db, 'teams', teamId);
    await updateDoc(teamRef, {
      hasSampleData: false,
      sampleDataDeletedAt: serverTimestamp(),
    });

    logger.info('[SampleData] Deleted sample data:', { teamId, ...result });
    return result;
  } catch (error) {
    logger.error('[SampleData] Failed to delete sample data:', error);
    throw error;
  }
}

/**
 * Get sample document IDs for a collection
 * Useful for UI to identify sample data
 */
export function getSampleDocumentIds(collectionName: string): string[] {
  const ids: string[] = [];

  // Map collection names to their ID prefixes and counts
  const collectionMap: Record<string, { prefix: string; count: number }> = {
    customers: { prefix: 'customer', count: 5 },
    vehicles: { prefix: 'vehicle', count: 5 },
    purchaseInvoices: { prefix: 'purchase', count: 5 },
    salesInvoices: { prefix: 'sales', count: 2 },
    appointments: { prefix: 'appointment', count: 5 },
    partStockLines: { prefix: 'stockline', count: 6 },
    workOrders: { prefix: 'workorder', count: 4 },
  };

  const config = collectionMap[collectionName];
  if (!config) {
    // Fallback for unknown collections
    return ids;
  }

  for (let i = 1; i <= config.count; i++) {
    ids.push(sampleId(config.prefix, i));
  }

  return ids;
}
